import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  Edit, 
  Save, 
  Plus, 
  Calendar, 
  Trash2,
  ExternalLink
} from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '@/config/api';

const toast = {
  success: (message: string) => {
    console.log('✅ Success:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 4000);
  },
  error: (message: string) => {
    console.error('❌ Error:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 5000);
  }
};

interface CertificationsTabProps {
  data: any;
  loading: boolean;
  isEditable: boolean;
  editMode: boolean;
  onEdit: () => void;
  onSave: (data: any) => void;
  onCancel: () => void;
  onRefresh?: () => void;
}

const CertificationsTab: React.FC<CertificationsTabProps> = ({ 
  data, 
  loading, 
  isEditable, 
  editMode, 
  onEdit, 
  onSave, 
  onCancel,
  onRefresh 
}) => {
  const [editData, setEditData] = useState(data);
  const updateEditData = (updater: (prev: any) => any) => setEditData((prev: any) => updater(prev));
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCertification, setNewCertification] = useState({
    certificationName: '',
    issuingOrganization: '',
    issueDate: '',
    expirationDate: '',
    doesNotExpire: false,
    credentialId: '',
    credentialUrl: '',
    description: ''
  });

  useEffect(() => {
    setEditData(data);
  }, [data]);

  if (loading) {
    return <Card><CardContent className="p-6">Loading certifications...</CardContent></Card>;
  }

  const handleSave = () => {
    onSave(editData);
  };

  const handleCancel = () => {
    setEditData(data);
    onCancel();
  };

  const handleCertificationChange = (index: number, field: string, value: any) => {
    updateEditData((prev: any) => ({
      ...prev,
      certifications: prev?.certifications?.map((cert: any, i: number) => 
        i === index ? { ...cert, [field]: value } : cert
      ) || []
    }));
  };

  const handleAddNewCertification = async () => {
    if (!newCertification.certificationName.trim()) {
      toast.error('Certification name is required');
      return;
    }
    
    if (!newCertification.issuingOrganization.trim()) {
      toast.error('Issuing organization is required');
      return;
    }
    
    if (!newCertification.issueDate.trim()) {
      toast.error('Issue date is required');
      return;
    }

    try {
      const url = buildApiUrl('certs/me/certifications');
      const headers = getApiHeaders(true);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(newCertification)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add certification');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('Certification added successfully');
        setNewCertification({
          certificationName: '',
          issuingOrganization: '',
          issueDate: '',
          expirationDate: '',
          doesNotExpire: false,
          credentialId: '',
          credentialUrl: '',
          description: ''
        });
        setShowAddForm(false);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || 'Failed to add certification');
      }
    } catch (error: any) {
      console.error('Error adding certification:', error);
      toast.error(`Error adding certification: ${error.message}`);
    }
  };

  const handleDeleteCertification = async (certificationId: number, certificationTitle: string) => {
    if (!certificationId || certificationId === undefined || certificationId === null) {
      toast.error('Cannot delete certification: Invalid certification ID');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the certification "${certificationTitle}"?`)) {
      return;
    }

    try {
      const url = buildApiUrl(`certs/me/certifications/${certificationId}`);
      const headers = getApiHeaders(true);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete certification');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Certification "${certificationTitle}" deleted successfully`);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || 'Failed to delete certification');
      }
    } catch (error: any) {
      console.error('Error deleting certification:', error);
      toast.error(`Error deleting certification: ${error.message}`);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Certifications ({data?.certifications?.length || 0})
        </CardTitle>
        <div className="flex gap-2">
          {isEditable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddForm(true)}
              className="bg-green-50 hover:bg-green-100 border-green-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Certification
            </Button>
          )}
          {isEditable && !editMode && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {data?.certifications?.length > 0 ? (
          <div className="space-y-4">
            {(editMode ? editData?.certifications : data.certifications)?.map((cert: any, index: number) => (
              <div key={cert.id || index} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    {editMode ? (
                      <input
                        type="text"
                        value={cert.certificationName || ''}
                        onChange={(e) => handleCertificationChange(index, 'certificationName', e.target.value)}
                        className="font-semibold text-lg mb-1 w-full border-b border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                        placeholder="Certification Name"
                      />
                    ) : (
                      <h3 className="font-semibold text-lg">{cert.certificationName}</h3>
                    )}
                    {editMode ? (
                      <input
                        type="text"
                        value={cert.issuingOrganization || ''}
                        onChange={(e) => handleCertificationChange(index, 'issuingOrganization', e.target.value)}
                        className="text-sm text-gray-600 mb-2 w-full border-b border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                        placeholder="Issuing Organization"
                      />
                    ) : (
                      <p className="text-sm text-gray-600 mb-2">{cert.issuingOrganization}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {editMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCertification(cert.id, cert.certificationName)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Issue Date</label>
                    {editMode ? (
                      <input
                        type="date"
                        value={cert.issueDate || ''}
                        onChange={(e) => handleCertificationChange(index, 'issueDate', e.target.value)}
                        className="w-full text-sm border-b border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                      />
                    ) : (
                      <p className="text-sm flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {cert.issueDate || 'Not specified'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Expiration Date</label>
                    {editMode ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={cert.expirationDate || ''}
                          onChange={(e) => handleCertificationChange(index, 'expirationDate', e.target.value)}
                          disabled={cert.doesNotExpire}
                          className="flex-1 text-sm border-b border-gray-300 focus:border-blue-500 outline-none bg-transparent disabled:opacity-50"
                        />
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={cert.doesNotExpire || false}
                            onChange={(e) => handleCertificationChange(index, 'doesNotExpire', e.target.checked)}
                            className="mr-1"
                          />
                          No expiry
                        </label>
                      </div>
                    ) : (
                      <p className="text-sm">
                        {cert.doesNotExpire ? 'Does not expire' : cert.expirationDate || 'Not specified'}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Credential ID</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={cert.credentialId || ''}
                        onChange={(e) => handleCertificationChange(index, 'credentialId', e.target.value)}
                        className="w-full text-sm border-b border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                        placeholder="Certificate ID"
                      />
                    ) : (
                      <p className="text-sm">{cert.credentialId || 'Not specified'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Credential URL</label>
                    {editMode ? (
                      <input
                        type="url"
                        value={cert.credentialUrl || ''}
                        onChange={(e) => handleCertificationChange(index, 'credentialUrl', e.target.value)}
                        className="w-full text-sm border-b border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                        placeholder="https://..."
                      />
                    ) : (
                      cert.credentialUrl ? (
                        <a 
                          href={cert.credentialUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View Credential
                        </a>
                      ) : (
                        <p className="text-sm">Not specified</p>
                      )
                    )}
                  </div>
                </div>

                {(editMode || cert.description) && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                    {editMode ? (
                      <textarea
                        value={cert.description || ''}
                        onChange={(e) => handleCertificationChange(index, 'description', e.target.value)}
                        rows={2}
                        className="w-full text-sm border border-gray-300 rounded-md px-2 py-1 focus:border-blue-500 outline-none"
                        placeholder="Brief description of the certification..."
                      />
                    ) : (
                      <p className="text-sm">{cert.description}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">No certifications available</p>
        )}

        {showAddForm && (
          <div className="border rounded-lg p-4 mt-4 bg-gray-50">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Certification
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Certification Name *
                </label>
                <input
                  type="text"
                  value={newCertification.certificationName}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, certificationName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., AWS Certified Developer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issuing Organization *
                </label>
                <input
                  type="text"
                  value={newCertification.issuingOrganization}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, issuingOrganization: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Amazon Web Services"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issue Date *
                </label>
                <input
                  type="date"
                  value={newCertification.issueDate}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, issueDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={newCertification.expirationDate}
                    onChange={(e) => setNewCertification(prev => ({ ...prev, expirationDate: e.target.value }))}
                    disabled={newCertification.doesNotExpire}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50"
                  />
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={newCertification.doesNotExpire}
                      onChange={(e) => setNewCertification(prev => ({ ...prev, doesNotExpire: e.target.checked, expirationDate: e.target.checked ? '' : prev.expirationDate }))}
                      className="mr-1"
                    />
                    No expiry
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credential ID
                </label>
                <input
                  type="text"
                  value={newCertification.credentialId}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, credentialId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Certificate number or ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credential URL
                </label>
                <input
                  type="url"
                  value={newCertification.credentialUrl}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, credentialUrl: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="https://..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newCertification.description}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Brief description of the certification..."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleAddNewCertification} className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Add Certification
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        
        {editMode && isEditable && (
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CertificationsTab;
